import React, { useContext } from 'react';
import {
  StyledLoginFormContainer,
  StyledError,
  StyledErrorContainer,
  StyledHeader,
  StyledLabel,
  StyledCheckBoxText,
  StyledButton,
  StyledCheckBoxContainer,
  StyledSpace,
  StyledTooltip,
  StyledPLeft,
  StyledInputCheckBox,
  StyledIcon,
  StyledInputAccessCode,
} from '../../../components/Styled/StyledLoginForm';
import { StyledEmail, StyledEmailContainer } from '../../../components/Styled/StyledAddCode';
import { StyledTermsOfUseButton } from '../../../components/Styled/StyledButtons';
import { Tooltip, Form, EnumTooltipPosition, Hyperlink } from 'react-magma-dom';
import { useNavigate } from '@reach/router';

import { HelpIcon } from 'react-magma-icons';
import { ErrorIcon } from 'react-magma-icons';
import accesscode from '../../../assets/accesscode.png';
import { useForm } from 'react-hook-form';
import { COLORS } from '../../../constants';
import { AccessCodeRequest } from '../../../types';
import { authAPI } from '../../../API/auth';
import AuthContext from '../../../contexts/authContext';

type FormData = {
  accessCode: string;
  schoolEmail: string;
  licence: boolean;
};

interface IAddCode {
  setIsTermsOfUseModalOpened: (isOpened: boolean) => void;
}

export const AddCode = (props: IAddCode): JSX.Element => {
  const { setIsTermsOfUseModalOpened } = props;

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isValid },
  } = useForm<FormData>({
    mode: 'onChange',
  });

  const { setDefaultStage, setAccess, setTrialDaysLeft } = useContext(AuthContext);
  const navigate = useNavigate();

  const tooltipContentLong = (
    <StyledTooltip>
      <StyledPLeft>Access code can be found on the inside cover of your BIG BOOK.</StyledPLeft>
      <StyledPLeft>
        Contact{' '}
        <Hyperlink target="_blank" to="https://cengage.com.au/contact-us">
          Cengage Support{' '}
        </Hyperlink>{' '}
        if you are having trouble.
      </StyledPLeft>
      <img src={accesscode as string} alt="accessCode" />
    </StyledTooltip>
  );

  const focusedColor = COLORS.seaBlue;
  const errorColor = COLORS.venetianRed;
  const defaultColor = COLORS.veryLightGrey;

  const registerOptions = {
    accessCode: {
      required: (
        <StyledErrorContainer>
          <ErrorIcon id="ErrorBlackIcon" title="Error ACCESS CODE INVALID" size={17} />{' '}
          <StyledError>ACCESS CODE INVALID</StyledError>
        </StyledErrorContainer>
      ),
      message: (
        <StyledErrorContainer>
          <ErrorIcon id="ErrorBlackIcon" title="Error ACCESS CODE INVALID" size={17} />{' '}
          <StyledError data-testid="error">{errors.accessCode?.message}</StyledError>
        </StyledErrorContainer>
      ),
    },
    licence: {
      required: '',
    },
  };

  const redeemCode = async (data: FormData): Promise<void> => {
    const accessCodeRequest: AccessCodeRequest = {
      accessCode: data.accessCode,
      agreement: data.licence,
    };

    try {
      const { data } = await authAPI.redeemCode(accessCodeRequest);

      const { defaultStage, access, trialDaysLeft } = data;
      setDefaultStage(defaultStage);
      setAccess(access);
      setTrialDaysLeft(trialDaysLeft);

      setDefaultStage(defaultStage);
      localStorage.setItem('access', access);
      localStorage.setItem('trialDaysLeft', trialDaysLeft);

      const path = (localStorage.getItem('path') || '/') + (localStorage.getItem('hash') || '');
      navigate(path);
      localStorage.setItem('path', '/');
    } catch (error) {
      const e = error as { status: number; data: { errors: { accessCode: string } } }; // Type assertion

      if (e.status === 400) {
        setError('accessCode', {
          type: 'manual',
          message: e.data.errors.accessCode,
        });
      } else {
        setError('accessCode', {
          type: 'manual',
          message: e.data.errors?.accessCode ?? 'An error occurred.',
        });
      }
    }
  };

  return (
    <StyledLoginFormContainer data-testid="add-code">
      <StyledHeader>Add Book</StyledHeader>
      <Form
        role="form"
        aria-label="Login Form"
        header=" "
        actions={
          <StyledButton
            data-testid="submit"
            onClick={handleSubmit(async data => {
              await redeemCode(data);
            })}
            disabled={!isValid}
          >
            Enter
          </StyledButton>
        }
      >
        <StyledEmailContainer>
          {' '}
          <StyledLabel>You’re logged in as</StyledLabel>
          <StyledEmail data-testid="email" id="inspectletIgnore">
            {localStorage.getItem('email')}
          </StyledEmail>
        </StyledEmailContainer>

        <StyledLabel>
          Please enter your PM Writing Digital Platform <b> Access Code</b>
          <Tooltip
            content={tooltipContentLong}
            isInverse
            position={EnumTooltipPosition.right}
            data-testid="add-code-tooltip"
          >
            <StyledIcon>
              <HelpIcon size={17} id="helpIcon" title="Help Information" />
            </StyledIcon>
          </Tooltip>
        </StyledLabel>
        <label htmlFor="accessCode">
          <StyledInputAccessCode
            role="textbox"
            aria-label="accessCode"
            key="0"
            id="accessCode"
            type="text"
            {...register('accessCode', {
              required: true,
              maxLength: 14,
              minLength: 14,
            })}
            borderColor={errors?.accessCode ? errorColor : defaultColor}
            color={errors?.accessCode ? errorColor : focusedColor}
            focusColor={errors?.accessCode ? errorColor : focusedColor}
          />
        </label>

        {errors?.accessCode ? (
          errors.accessCode?.message ? (
            registerOptions.accessCode.message
          ) : (
            registerOptions.accessCode.required
          )
        ) : (
          <StyledSpace />
        )}
        <StyledCheckBoxContainer>
          <StyledInputCheckBox
            role="checkbox"
            aria-checked="false"
            key="2"
            type="checkbox"
            aria-label="checkbox for licence"
            {...register('licence', { required: true })}
          />
          <StyledCheckBoxText>
            Please read the{' '}
            <StyledTermsOfUseButton
              type="button"
              name="terms of use button"
              onClick={() => {
                setIsTermsOfUseModalOpened(true);
              }}
            >
              terms of use
            </StyledTermsOfUseButton>
            . By using the PM Writing software, you agree to be bound by the terms of this licence.
          </StyledCheckBoxText>
        </StyledCheckBoxContainer>
      </Form>
    </StyledLoginFormContainer>
  );
};
